import {
    Component,
    OnInit,
    Injectable,
    EventEmitter,
    Output,
    Input,
    ChangeDetectionStrategy,
    OnDestroy,
    ViewChild,
    ElementRef,
} from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlattener, MatTreeFlatDataSource } from '@angular/material/tree';
import { of as ofObservable, Observable, BehaviorSubject, Subscription } from 'rxjs';
import { gisUserSettings, mapLayerList, ArcGISFeatureLayer, ArcGISWebMap, MapLayerLegendInfo } from 'app/shared/models/gis-service-list';
import { ArcGISLayerData } from 'app/shared/models/arcgis-types';
import { ActivatedRoute } from '@angular/router';
import {
    CompositeLocComponent,
    CompositeLocComponentInput,
    CompositeLocCreateInput,
} from 'app/shared/components/map/composite-loc/composite-loc.component';
import { MatLegacyDialog as MatDialog, MatLegacyDialogConfig as MatDialogConfig } from '@angular/material/legacy-dialog';
import { GISService, LegendType } from 'app/shared/services/gis-service';
import { filter, first } from 'rxjs/operators';
import { Locations } from 'app/shared/models/locations';
import { MapService } from 'app/shared/services/map.service';
import { AddLocationModalData } from 'app/shared/models/add-location';
import { LocationCardService } from 'app/shared/components/location-card/services/location-card.service';
import { TrackBy } from 'app/shared/utils/track-by';
import { LocationCardContainerDialogComponent } from 'app/shared/components/location-card/components/location-card-container/location-card-container.component';
import { UsersService } from 'app/pages/admin/users.service';
import { IComponentModalResult } from 'app/shared/models/modal';

/**
 * Node for to-do item
 */
export class TodoItemNode {
    children: TodoItemNode[];
    item: string;
    checked: boolean;
    legendInfo: MapLayerLegendInfo[];
    showLegend: boolean;
    selection: boolean;
    opacity: number;
    isArcGIS?: boolean;
    layerData?: ArcGISLayerData | ArcGISFeatureLayer | ArcGISWebMap | null;
    assetId?: string;
    assetType?: string;
    showLabels?: boolean;
    labelField?: string;
}

/** Flat to-do item node with expandable and level information */
export class TodoItemFlatNode {
    item: string;
    level: number;
    expandable: boolean;
    checked: boolean;
    legendInfo: MapLayerLegendInfo[];
    showLegend: boolean;
    selection: boolean;
    opacity: number;
    isArcGIS?: boolean;
    layerData?: ArcGISLayerData | ArcGISFeatureLayer | ArcGISWebMap | null;
    assetId?: string;
    assetType?: string;
    showLabels?: boolean;
    labelField?: string;
}

/**
 * The Json object for to-do list data.
 */
let TREE_DATA: mapLayerList[] = [];

/**
 * Checklist database, it can build a tree structured Json object.
 * Each node in Json object represents a to-do item or a category.
 * If a node is a category, it has children items and new items can be added under the category.
 */
@Injectable({ providedIn: 'root' })
export class ChecklistDatabase {
    dataChange: BehaviorSubject<TodoItemNode[]> = new BehaviorSubject<TodoItemNode[]>([]);

    get data(): TodoItemNode[] {
        return this.dataChange.value;
    }

    constructor() {
        this.initialize();
    }

    initialize() {
        this.dataupdate(TREE_DATA);
    }

    dataupdate(datas: mapLayerList[]): void {
        const data = this.buildTree(datas, 0);
        this.dataChange.next(data);
    }

    /**
     * Build the file structure tree. The `value` is the Json object, or a sub-tree of a Json object.
     * The return value is the list of `TodoItemNode`.
     */
    buildTree(value: mapLayerList[], level: number): TodoItemNode[] {
        const data: TodoItemNode[] = [];
        if (value.length > 0) {
            value.forEach((lyr) => {
                const dataitem = this.buildFileTree(lyr, level);
                data.push(dataitem);
            });
        }
        return data;
    }

    buildFileTree(value: mapLayerList, level: number): TodoItemNode {
        const node = new TodoItemNode();
        node.item = value.name;
        node.checked = value.checked;
        node.showLegend = value.showLegend;
        node.legendInfo = value.legendInfo;
        node.selection = value.selection;
        node.opacity = value.opacity;
        node.isArcGIS = value.isArcGIS;
        node.layerData = value.layerData;
        node.assetId = value.assetId;
        node.assetType = value.assetType;
        node.showLabels = value.showLabels;
        node.labelField = value.labelField;
        if (value.child && value.child.length > 0) {
            node.children = this.buildTree(value.child, 1);
        }

        return node;
    }

    /** Add an item to to-do list */
    insertItem(parent: TodoItemNode, name: string) {
        const child = <TodoItemNode>{ item: name };
        if (parent.children) {
            parent.children.push(child);
            this.dataChange.next(this.data);
        }
    }

    updateItem(node: TodoItemNode, name: string) {
        node.item = name;
        this.dataChange.next(this.data);
    }
}

/**
 * @title Tree with checkboxes
 */

@Component({
    selector: 'app-location-layer-list',
    templateUrl: './location-layer-list.component.html',
    styleUrls: ['./location-layer-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationLayerListComponent implements OnInit, OnDestroy {
    public LegendType = LegendType;
    private subscriptions = new Subscription();
    @ViewChild('treeNode') public firstNode: ElementRef<HTMLDivElement>;
    @Input() public locations = new Array<Locations>();
    @Input() private allRainLocations = new Array<Locations>();
    public assignedRainGauge = new Array<Locations>();
    customerId: number;
    @Output() public layerSelection = new EventEmitter<mapLayerList[]>();
    @Output() public labelsChanged = new EventEmitter();
    @Output() public opacityChange = new EventEmitter<mapLayerList[]>();
    @Output() public zoomtolayer = new EventEmitter<mapLayerList[]>();
    @Output() public refreshMap = new EventEmitter();
    @Output() public removeLayer = new EventEmitter<mapLayerList>();
    @Output() public arcgisLabelsChanged = new EventEmitter<mapLayerList>();
    @Input() public mapLayerslist = {};
    @Input() public availablePageHint: boolean;
    /* const layerlist = [{
         visible: false,
         name:'Monitor',
         selection:false
     }]*/
    /** Map from flat node to nested node. This helps us finding the nested node to be modified */
    flatNodeMap: Map<TodoItemFlatNode, TodoItemNode> = new Map<TodoItemFlatNode, TodoItemNode>();

    /** Map from nested node to flattened node. This helps us to keep the same object for selection */
    nestedNodeMap: Map<TodoItemNode, TodoItemFlatNode> = new Map<TodoItemNode, TodoItemFlatNode>();

    /** A selected parent node to be inserted */
    selectedParent: TodoItemFlatNode | null = null;

    /** The new item's name */
    newItemName = '';

    treeControl: FlatTreeControl<TodoItemFlatNode>;

    treeFlattener: MatTreeFlattener<TodoItemNode, TodoItemFlatNode>;

    dataSource: MatTreeFlatDataSource<TodoItemNode, TodoItemFlatNode>;

    /** The selection for checklist */
    checklistSelection = new SelectionModel<TodoItemFlatNode>(true /* multiple */);
    userSettings: gisUserSettings;
    public trackByLabel = TrackBy.byUniqueKey<{ label: string }>()('label');

    constructor(
        private database: ChecklistDatabase,
        private activatedRoute: ActivatedRoute,
        private dialog: MatDialog,
        private gisService: GISService,
        private mapService: MapService,
        private locationCardService: LocationCardService,
        public usersService: UsersService,
    ) {}

    ngOnInit() {
        const routeParamsSubs = this.activatedRoute.queryParams.subscribe((data) => {
            if (!data) return;

            this.customerId = Number(data.c);
        });

        const settingSubs = this.gisService.gisUserSettingsSubject$.pipe(filter(v => !!v))
            .subscribe((userSettings: gisUserSettings) => {
                this.userSettings = userSettings;
            });
        this.subscriptions.add(settingSubs);
        this.subscriptions.add(routeParamsSubs);
        this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel, this.isExpandable, this.getChildren);
        this.treeControl = new FlatTreeControl<TodoItemFlatNode>(this.getLevel, this.isExpandable);
        this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
        this.database.dataChange.subscribe((data) => {
            this.dataSource.data = data;
            this.checkAll();
        });
    }
    getLevel = (node: TodoItemFlatNode) => {
        return node.level;
    };

    isExpandable = (node: TodoItemFlatNode) => {
        return node.expandable;
    };

    isChecked = (node: TodoItemFlatNode) => {
        return node.checked;
    };

    getChildren = (node: TodoItemNode): Observable<TodoItemNode[]> => {
        return ofObservable(node.children);
    };

    hasChild = (_: number, _nodeData: TodoItemFlatNode) => {
        return _nodeData.expandable;
    };

    hasNoContent = (_: number, _nodeData: TodoItemFlatNode) => {
        return _nodeData.item === '';
    };

    /**
     * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
     */
    transformer = (node: TodoItemNode, level: number) => {
        const flatNode =
            this.nestedNodeMap.has(node) && this.nestedNodeMap.get(node)!.item === node.item
                ? this.nestedNodeMap.get(node)!
                : new TodoItemFlatNode();
        flatNode.item = node.item;
        flatNode.checked = node.checked;
        flatNode.showLegend = node.showLegend;
        flatNode.legendInfo = node.legendInfo;
        flatNode.selection = node.selection;
        flatNode.opacity = node.opacity;
        flatNode.isArcGIS = node.isArcGIS;
        flatNode.layerData = node.layerData;
        flatNode.assetId = node.assetId;
        flatNode.assetType = node.assetType;
        flatNode.showLabels = node.showLabels;
        flatNode.labelField = node.labelField;
        flatNode.level = level;
        flatNode.expandable = !!node.children;
        this.flatNodeMap.set(flatNode, node);
        this.nestedNodeMap.set(node, flatNode);
        return flatNode;
    };

    checkAll() {
        for (const node of this.treeControl.dataNodes) {
            if (!this.checklistSelection.isSelected(node) && node.checked) {
                this.checklistSelection.toggle(node);
            }
        }
    }

    /** Whether all the descendants of the node are selected */
    descendantsAllSelected(node: TodoItemFlatNode): boolean {
        const descendants = this.treeControl.getDescendants(node);
        return descendants.every((child) => child.checked);
    }

    /** Whether part of the descendants are selected */
    descendantsPartiallySelected(node: TodoItemFlatNode): boolean {
        const descendants = this.treeControl.getDescendants(node);
        const result = descendants.some((child) => child.checked);
        return result && !this.descendantsAllSelected(node);
    }

    /** Toggle the to-do item selection. Select/deselect all the descendants node */
    selectAllChildren(node: TodoItemFlatNode): void {
        // First verify whether it should be towards checked or unchecked
        const currentState = this.descendantsAllSelected(node);
        const nodeChildren = this.treeControl.getDescendants(node);

        // Then move all of the children boxes to the correct direction
        const layerList = [];

        if (node.isArcGIS) {
            node.checked = !currentState;

            const childLayers = [];
            for (const child of nodeChildren) {
                child.checked = !currentState;
                childLayers.push({
                    checked: child.checked,
                    name: child.item,
                    selection: child.selection,
                    legendInfo: child.legendInfo,
                    showLegend: child.showLegend,
                    opacity: child.opacity,
                    isArcGIS: child.isArcGIS,
                    layerData: child.layerData
                });
            }

            layerList.push({
                checked: !currentState,
                name: node.item,
                selection: node.selection,
                legendInfo: node.legendInfo,
                showLegend: node.showLegend,
                opacity: node.opacity,
                isArcGIS: node.isArcGIS,
                assetId: node.assetId,
                assetType: node.assetType,
                child: childLayers
            });

        } else {
            for (const child of nodeChildren) {
                child.checked = !currentState;
                layerList.push({
                    checked: child.checked,
                    name: child.item,
                    selection: child.selection,
                    legendInfo: child.legendInfo,
                    showLegend: child.showLegend,
                    opacity: child.opacity,
                });
            }
        }

        // Finally emit changes back to parent component to actually affect visibility
        this.layerSelection.emit(layerList);
    }

    public toggleNode(node, actionType: number) {
        const layerlist = [
            {
                checked: node.checked,
                name: node.item,
                selection: node.selection,
                showLegend: node.showLegend,
                legendInfo: node.legendInfo,
                opacity: node.opacity,
                isArcGIS: node.isArcGIS,
                layerData: node.layerData,
                assetId: node.assetId,
                assetType: node.assetType,
            },
        ];
        switch (actionType) {
            case 0: {
                // change node
                this.checklistSelection.toggle(node);
                this.layerSelection.emit(layerlist);
                break;
            }
            case 1: {
                // zoom layer
                this.zoomtolayer.emit(layerlist);
                break;
            }
            case 2: {
                // opacity change
                this.opacityChange.emit(layerlist);
                break;
            }
        }
    }

    public async addCompositeLocation(): Promise<void> {
        if (!this.usersService.isMonitorEditor.getValue()) {
            return;
        }

        this.setupRainGaugeLocations();

        const data: CompositeLocCreateInput = {
            customerId: this.customerId,
            enableSelectOnMap: true,
        };
        const dialogOptions: MatDialogConfig<CompositeLocComponentInput> = {
            disableClose: true,
            data,
            hasBackdrop: false
        };

        const userSettings = await this.gisService.gisUserSettingsGet();

        if (this.gisService.locationCardPosition) {
            dialogOptions.position = this.locationCardService.checkLocationCardPosition(false, this.gisService.locationCardPosition);
        } else if (userSettings && userSettings.locationPositionX && userSettings.locationPositionY) {
            dialogOptions.position = this.locationCardService.getLocationCardPosition(false, userSettings);
        }

        this.dialog
            .open<CompositeLocComponent, CompositeLocComponentInput, IComponentModalResult>(
                CompositeLocComponent,
                dialogOptions,
            )
            .afterClosed()
            .subscribe((res) => {
                if (res && res.success) {
                    this.refreshMap.emit(true);
                }
            });
    }

    public async addEditLocation(): Promise<void> {
        if (!this.usersService.isMonitorEditor.getValue()) {
            return;
        }

        this.setupRainGaugeLocations();
        const editTritonLocationComponentMapData: AddLocationModalData = {
            rainGaugeLocations: this.allRainLocations,
            customerId: this.customerId,
            editMode: false,
            isFromCustomerEditor: false,
            enableSelectOnMap: true,
        };

        const dialogOptions: MatDialogConfig<AddLocationModalData> = {
            disableClose: true,
            data: editTritonLocationComponentMapData,
            hasBackdrop: false,
        };

        const userSettings = await this.gisService.gisUserSettingsGet();

        if (this.gisService.locationCardPosition) {
            dialogOptions.position = this.locationCardService.checkLocationCardPosition(true, this.gisService.locationCardPosition);
        } else if (userSettings && userSettings.locationPositionX && userSettings.locationPositionY) {
            dialogOptions.position = this.locationCardService.getLocationCardPosition(true, userSettings);
        }

        if (this.mapService.addEditLocationDialog) {
            this.mapService.addEditLocationDialog.close();
        }

        this.mapService.addEditLocationDialog = this.dialog.open<
            LocationCardContainerDialogComponent,
            AddLocationModalData,
            boolean
        >(LocationCardContainerDialogComponent, dialogOptions);
        this.mapService.addEditLocationDialog
            .afterClosed()
            .pipe(first())
            .subscribe((result: boolean) => {
                this.mapService.addEditLocationDialog = null;
                if (result) {
                    this.refreshMap.emit(true);
                }
            });
    }

    public labelsClick() {
        const { showLocationLabels } = this.userSettings;
        this.userSettings.showLocationLabels = !showLocationLabels;
        this.gisService.updateGisUserSettings({...this.userSettings, showLocationLabels: !showLocationLabels}).subscribe(() => {
            this.labelsChanged.emit(null);
        });
    }

    public toggleArcGISLabels(node: TodoItemFlatNode): void {
        node.showLabels = !node.showLabels;

        const layerInfo: mapLayerList = {
            name: node.item,
            checked: node.checked,
            selection: node.selection,
            legendInfo: node.legendInfo as MapLayerLegendInfo[],
            showLegend: node.showLegend,
            opacity: node.opacity,
            isArcGIS: node.isArcGIS,
            layerData: node.layerData,
            showLabels: node.showLabels,
            labelField: node.labelField
        };

        this.arcgisLabelsChanged.emit(layerInfo);
    }

    public removeArcGISLayer(node: TodoItemFlatNode): void {
        const descendants = this.treeControl.getDescendants(node);
        const childLayers = [];

        for (const child of descendants) {
            childLayers.push({
                name: child.item,
                checked: child.checked,
                selection: child.selection,
                legendInfo: child.legendInfo as { label: string; url: string }[],
                showLegend: child.showLegend,
                opacity: child.opacity,
                isArcGIS: child.isArcGIS,
                layerData: child.layerData
            });
        }

        const layerInfo: mapLayerList = {
            name: node.item,
            checked: node.checked,
            selection: node.selection,
            legendInfo: node.legendInfo as { label: string; url: string }[],
            showLegend: node.showLegend,
            opacity: node.opacity,
            isArcGIS: node.isArcGIS,
            assetId: node.assetId,
            assetType: node.assetType,
            child: childLayers
        };

        this.removeLayer.emit(layerInfo);
    }

    private setupRainGaugeLocations() {
        // check if rain guage locations are already filled and if so exit immediately
        if (this.assignedRainGauge && this.assignedRainGauge.length > 0) {
            return;
        }

        // check if locations exist
        if (this.locations) {
            // make the filtered array for assignedRainGauge.
            this.assignedRainGauge = this.locations.filter((location) => {
                if (location && location.series && location.series.toLowerCase().indexOf('rainalert') > -1) {
                    return location;
                }
            });
        }
    }

    loaddata(layer: mapLayerList[]): void {
        TREE_DATA = layer;
        this.database.dataupdate(layer);
    }

    ngOnDestroy() {
        if (this.mapService.addEditLocationDialog) {
            this.mapService.addEditLocationDialog.close();
            this.mapService.addEditLocationDialog = null;
        }
        this.subscriptions.unsubscribe();
    }
}
